import React, { useEffect, useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { API } from '../api/api';

const ROLE_OPTIONS = ['Udvikler', 'Projektleder'];

// Mini admin panel: lists everyone who has ever logged into the calendar app
// and lets a Projektleder promote/demote their role. Role gates access to
// the "Business Development" timelog type (see EventFormModal/SeriesModal).
export default function AdminPanel({ isOpen, onClose }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingZuid, setSavingZuid] = useState(null);

  const loadUsers = useCallback(() => {
    setIsLoading(true);
    setError('');
    API.fetchAdminUsers()
      .then((data) => {
        if (data.success) {
          setUsers(data.data || []);
        } else {
          setError(data.message || 'Failed to load users');
        }
      })
      .catch((err) => setError(err.message || 'Failed to load users'))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (isOpen) loadUsers();
  }, [isOpen, loadUsers]);

  if (!isOpen) return null;

  const handleRoleChange = (zuid, newRole) => {
    setSavingZuid(zuid);
    setError('');
    API.setUserRole(zuid, newRole)
      .then((data) => {
        if (data.success) {
          setUsers((prev) => prev.map((u) => (u.zuid === zuid ? { ...u, role: newRole } : u)));
        } else {
          setError(data.message || 'Failed to update role');
        }
      })
      .catch((err) => setError(err.message || 'Failed to update role'))
      .finally(() => setSavingZuid(null));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-[2500] flex items-center justify-center p-4">
      <div className="bg-[#0D1A27] border border-[#273C61] rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#273C61]">
          <h3 className="text-lg font-semibold text-white">Brugere &amp; roller</h3>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          <p className="text-white/60 text-sm mb-4">
            Kun brugere med rollen <span className="text-white/90 font-medium">Projektleder</span> kan vælge{' '}
            <span className="text-white/90 font-medium">Business Development</span> som type ved tidsregistrering.
            Alle nye brugere starter som <span className="text-white/90 font-medium">Udvikler</span>.
          </p>

          {error && (
            <div className="text-red-400 text-sm mb-4 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-white/50 text-sm">Indlæser...</div>
          ) : users.length === 0 ? (
            <div className="text-white/50 text-sm">Ingen brugere fundet endnu.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 border-b border-[#273C61]">
                  <th className="py-2 pr-3 font-medium">Navn</th>
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Rolle</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.zuid} className="border-b border-[#273C61]/50">
                    <td className="py-2 pr-3 text-white">{u.name || '—'}</td>
                    <td className="py-2 pr-3 text-white/70">{u.email || '—'}</td>
                    <td className="py-2 pr-3">
                      {u.isSuperAdmin ? (
                        <span
                          className="inline-flex items-center gap-1 text-white/90 font-medium"
                          title="Fast Projektleder — kan ikke ændres her"
                        >
                          Projektleder <span className="text-[#FF7800]">★</span>
                        </span>
                      ) : (
                        <select
                          value={u.role}
                          disabled={savingZuid === u.zuid}
                          onChange={(e) => handleRoleChange(u.zuid, e.target.value)}
                          className="bg-[#111e2e] border border-[#273C61] rounded px-2 py-1 text-white disabled:opacity-50"
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#273C61] flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-white bg-[#205799] rounded hover:bg-[#26689c] transition-colors"
          >
            Luk
          </button>
        </div>
      </div>
    </div>
  );
}
